import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiConfigService } from './api-config.service';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfigService
  ) { }

  /**
   * Generic GET request
   * @param endpoint API endpoint (without base URL)
   * @param params Optional query parameters
   * @returns Observable with response data
   */
  get<T = any>(endpoint: string, params?: { [key: string]: any }): Observable<T> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.get<T>(this.apiConfig.getUrl(endpoint), {
      headers,
      params: httpParams
    });
  }

  /**
   * Generic POST request
   * @param endpoint API endpoint (without base URL)
   * @param body Request body
   * @returns Observable with response data
   */
  post<T = any>(endpoint: string, body: any): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<T>(this.apiConfig.getUrl(endpoint), body, { headers });
  }

  /**
   * Generic PUT request
   * @param endpoint API endpoint (without base URL)
   * @param body Request body
   * @returns Observable with response data
   */
  put<T = any>(endpoint: string, body: any): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.put<T>(this.apiConfig.getUrl(endpoint), body, { headers });
  }

  /**
   * Generic PATCH request
   * @param endpoint API endpoint (without base URL)
   * @param body Request body
   * @returns Observable with response data
   */
  patch<T = any>(endpoint: string, body: any): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.patch<T>(this.apiConfig.getUrl(endpoint), body, { headers });
  }

  /**
   * Generic DELETE request
   * @param endpoint API endpoint (without base URL)
   * @param body Optional request body (supported by Angular HttpClient)
   * @returns Observable with response data
   */
  delete<T = any>(endpoint: string, body?: unknown): Observable<T> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const opts: { headers: HttpHeaders; body?: unknown } = { headers };
    if (body !== undefined) opts.body = body;
    return this.http.delete<T>(this.apiConfig.getUrl(endpoint), opts);
  }

  /**
   * Upload file
   * @param endpoint API endpoint (without base URL)
   * @param file File to upload
   * @param additionalData Optional additional form data
   * @returns Observable with response data
   */
  uploadFile<T = any>(endpoint: string, file: File, additionalData?: { [key: string]: any }): Observable<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    return this.http.post<T>(this.apiConfig.getUrl(endpoint), formData);
  }

  /**
   * Get full URL for an endpoint
   * @param endpoint API endpoint (without base URL)
   * @returns Full URL string
   */
  getUrl(endpoint: string): string {
    return this.apiConfig.getUrl(endpoint);
  }

  /**
   * Get popular cities
   * @param isPopular Whether to fetch only popular cities (default: true)
   * @returns Observable with cities data
   */
  getCities(isPopular: boolean = true): Observable<any> {
    return this.get('cities', { is_popular: isPopular });
  }

  /** Get countries (for international billing dropdown). */
  getCountries(): Observable<any> {
    return this.get('get/country/state/city');
  }

  getStates(country: string) {
    return this.get('get/country/state/city', {
      country
    });
  }

  getCitiesByCountryAndState(country: string, state: string) {
    return this.get('get/country/state/city', {
      country,
      state
    });
  }


  getCitiesByCountry(countryId: number): Observable<any> {
    return this.get('states', { country_id: countryId });
  }

  /**
   * Get recommended events for a city
   * @param cityId City ID
   * @returns Observable with recommended events data
   */
  getRecommendedEvents(city: string): Observable<any> {
    return this.get('events/recommended', { city: city });
  }

  /**
   * Get categories
   * @param limit Number of categories to fetch (optional)
   * @param offset Offset for pagination (optional)
   * @returns Observable with categories data
   */
  getCategories(limit?: number, offset?: number): Observable<any> {
    const params: { [key: string]: any } = {};
    if (limit !== undefined && limit !== null) {
      params['limit'] = limit;
    }
    if (offset !== undefined && offset !== null) {
      params['offset'] = offset;
    }
    return this.get('categories', params);
  }

  /**
   * Get events by city ID
   * @param cityId City ID to filter events
   * @returns Observable with events data
   */
  getEventsByCity(cityId: number): Observable<any> {
    return this.get('events', { city_id: cityId });
  }

  /**
   * Get cities with pagination
   * @param limit Number of cities to fetch (optional)
   * @param offset Offset for pagination (optional)
   * @returns Observable with cities data
   */
  getCitiesPaginated(limit?: number, offset?: number): Observable<any> {
    const params: { [key: string]: any } = {};
    if (limit !== undefined && limit !== null) {
      params['limit'] = limit;
    }
    if (offset !== undefined && offset !== null) {
      params['offset'] = offset;
    }
    return this.get('cities', params);
  }

  /**
   * Search events, artists, venues
   * @param query Search query string
   * @param type Search type (optional, defaults to 'all')
   * @returns Observable with search results
   */
  search(query: string, type: string = 'all'): Observable<any> {
    const params: { [key: string]: any } = {
      q: query,
      type: type
    };
    return this.get('search', params);
  }

  /**
   * Get event by ID
   * @param eventId Event ID or string reference (URL segment)
   * @returns Observable with event data
   */
  getEventById(eventId: string | number): Observable<any> {
    return this.get(`get/events/${encodeURIComponent(String(eventId))}`);
  }

  /**
   * Get tour cities / similar events for an event
   * @param eventId Event ID or string reference (URL segment)
   * @returns Observable with tour cities data
   */
  getTourCities(eventId: string | number): Observable<any> {
    return this.get(`events/${encodeURIComponent(String(eventId))}/tour-cities`);
  }

  /**
   * Get shows for an event on a specific date
   * @param eventId Event ID or string reference (URL segment)
   * @param date Date in YYYY-MM-DD format
   * @returns Observable with shows data for that date
   */
  getEventShowsByDate(eventId: string | number, date: string): Observable<any> {
    return this.get(`events/${encodeURIComponent(String(eventId))}/shows/date/${encodeURIComponent(date)}`);
  }

  /**
   * Get artists with pagination
   * @param limit Number of artists to fetch (optional)
   * @param offset Offset for pagination (optional)
   * @returns Observable with artists data
   */
  getArtists(limit?: number, offset?: number): Observable<any> {
    const params: { [key: string]: any } = {};
    if (limit !== undefined && limit !== null) {
      params['limit'] = limit;
    }
    if (offset !== undefined && offset !== null) {
      params['offset'] = offset;
    }
    return this.get('artists', params);
  }

  /**
   * Get genres with pagination
   * @param limit Number of genres to fetch (optional)
   * @param offset Offset for pagination (optional)
   * @returns Observable with genres data
   */
  getGenres(limit?: number, offset?: number): Observable<any> {
    const params: { [key: string]: any } = {};
    if (limit !== undefined && limit !== null) {
      params['limit'] = limit;
    }
    if (offset !== undefined && offset !== null) {
      params['offset'] = offset;
    }
    return this.get('get/genres', params);
  }

  /**
   * Post/update user profile
   * POST /api/v1/users/profile
   * @param profile Profile data (email, first_name, last_name, full_name, profile_picture, city_id, etc.)
   * @returns Observable with response data
   */
  postProfile(profile: {
    email?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    profile_picture?: string;
    password?: string;
    is_active?: boolean;
    email_verified?: boolean;
    referral_code?: string;
    gender?: string;
    marital_status?: boolean;
    birthday?: string;
    city_id?: number;
    city?: string;
    phone?: string;
    [key: string]: any;
  }): Observable<any> {
    return this.post('users/profile', profile);
  }

  /**
   * Get user orders
   * @param page Page number (default 1)
   * @param limit Items per page (default 10)
   * @param status Order status: 'all' | 'upcoming' | 'completed' | 'cancelled'
   * @returns Observable with orders data
   */
  getOrders(page: number = 1, limit: number = 10, status?: string): Observable<any> {
    const params: { [key: string]: any } = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (status && status !== 'all') {
      params['status'] = status;
    }
    return this.get('orders', params);
  }

}
